import db from "../db/database";
import {
	fetchFromBusinessCentral,
	sendToBusinessCentral,
} from "../utils/businessCentralUtils";
import { getPendingSync, isOnline } from "./syncService";

// Función para formatear fecha en formato YYYY-MM-DD
const formatDate = (date) => {
	if (!date) return "";
	const d = new Date(date);
	// Formatear como YYYY-MM-DD
	return (
		d.getFullYear() +
		"-" +
		("0" + (d.getMonth() + 1)).slice(-2) +
		"-" +
		("0" + d.getDate()).slice(-2)
	);
};

// Función para transformar un TPV al formato esperado por Business Central
const transformTPVForBC = (tpv) => {
	// Transformamos el TPV al formato esperado por BC
	const transformedTPV = {
		No_: tpv.No || "TEMP", // Si no tiene No, usamos TEMP para que BC asigne uno
		No: tpv.No || "TEMP", // Si no tiene No, usamos TEMP para que BC asigne uno
		"No.": tpv.No || "TEMP", // Si no tiene No, usamos TEMP para que BC asigne uno
		nombre: tpv.nombre,
		direccion: tpv.direccion || "",
		direccion2: tpv.direccion2 || "",
		ciudad: tpv.ciudad || "",
		codigoPostal: tpv.codigoPostal || "",
		provincia: tpv.provincia || "",
		pais: tpv.pais || "",
		telefono: tpv.telefono || "",
		Mobil: tpv.movil || "",
		email: tpv.email || "",
		sitioWeb: tpv.sitioWeb || "",
		nifCif: tpv.nifCif || "",
		contacto: tpv.contacto || "",
		notas: tpv.notas || "",
		fechaAlta: tpv.fechaAlta
			? formatDate(tpv.fechaAlta)
			: formatDate(new Date()),
		locationCode: tpv.locationCode || "",
		noSeries: tpv.noSeries || "",
		margenCierre: tpv.margenCierre || 0,
		Deleted: tpv.deleted,
	};

	// Creamos el formato final esperado por el end point
	return {
		data: JSON.stringify({ TPVs: [transformedTPV] }),
	};
};

const mapServerTPVToLocal = (serverTPV) => ({
	nombre: serverTPV.nombre,
	direccion: serverTPV.direccion ?? "",
	direccion2: serverTPV.direccion2 ?? "",
	ciudad: serverTPV.ciudad ?? "",
	codigoPostal: serverTPV.codigoPostal ?? serverTPV.codigo_postal ?? "",
	provincia: serverTPV.provincia ?? "",
	pais: serverTPV.pais ?? "",
	telefono: serverTPV.telefono ?? "",
	movil: serverTPV.Mobil ?? "",
	email: serverTPV.email ?? "",
	sitioWeb: serverTPV.sitioWeb ?? serverTPV.sitio_web ?? "",
	nifCif: serverTPV.nifCif ?? serverTPV.nif_cif ?? "",
	contacto: serverTPV.contacto ?? "",
	notas: serverTPV.notas ?? "",
	fechaAlta: serverTPV.fechaAlta || serverTPV.fecha_alta
		? new Date(serverTPV.fechaAlta || serverTPV.fecha_alta)
		: null,
	locationCode: serverTPV.locationCode ?? serverTPV.location_code ?? "",
	noSeries: serverTPV.noSeries ?? serverTPV.no_series ?? "",
	margenCierre: serverTPV.margenCierre ?? 0,
	customerPriceGroup: serverTPV.CustomerPriceGroup ?? "",
	defaultCustomer: serverTPV.DefaultCustomer ?? "",
	noSerieFacturas: serverTPV.NoSerieFacturas ?? "",
	noSerieNotasCredito: serverTPV.NoSerieNotasCredito ?? "",
	lastModified: new Date(),
	lastSynced: new Date(),
});

export const syncTPVs = async () => {
	if (!isOnline()) {
		return false;
	}

	try {
		const pendingTPVs = await getPendingSync("tpvs");

		// Enviar TPVs pendientes uno por uno
		if (pendingTPVs.length > 0) {
			console.log(`Sincronizando ${pendingTPVs.length} Tiendas pendientes...`);

			for (const tpv of pendingTPVs) {
				try {
					// Si el TPV está marcado como eliminado, lo eliminamos directamente
					if (tpv.deleted) {
						await db.tpvs.delete(tpv.id);
						continue;
					}

					// Enviar TPV individual a Business Central
					const response = await sendToBusinessCentral(
						"Importaciones_insertaTPV",
						transformTPVForBC(tpv),
						true
					);

					const returnedTPVNo = response?.value;
					const tpvNo = returnedTPVNo || tpv.No;
					const canMarkSynced = Boolean(response && tpvNo);

					if (canMarkSynced) {
						await db.tpvs.update(tpv.id, {
							No: tpvNo,
							lastModified: new Date(),
							lastSynced: new Date(),
							pendingSync: 0,
						});
					} else {
						console.error(
							`Error al sincronizar Tienda ${tpv.id}: Respuesta inválida de BC`
						);
					}
				} catch (error) {
					console.error(`Error al sincronizar Tienda ${tpv.id}:`, error);
				}
			}
		}

		// Obtener TPVs del servidor solo después de sincronizar los pendientes
		const serverTPVs = await fetchFromBusinessCentral("ListaTPV");
		const localTPVs = await db.tpvs.toArray();

		// Mapa para buscar TPVs locales por No rápidamente
		const localTPVsByNo = {};
		localTPVs.forEach((tpv) => {
			if (tpv.No) {
				localTPVsByNo[tpv.No] = tpv;
			}
		});

		// Actualizar TPVs existentes y añadir nuevos desde el servidor
		for (const serverTPV of serverTPVs) {

			const localTPV = localTPVsByNo[serverTPV.No];
			const mappedTPV = mapServerTPVToLocal(serverTPV);

			if (localTPV) {
				if (localTPV.pendingSync === 0) {
					await db.tpvs.update(localTPV.id, mappedTPV);
				}
			} else {
				await db.tpvs.add({
					No: serverTPV.No,
					...mappedTPV,
					pendingSync: 0,
					deleted: false,
				});
			}
		}

		return true;
	} catch (error) {
		console.error("Error al sincronizar Tiendas:", error);
		return false;
	}
};
